/* eslint-disable no-case-declarations */
import useProjectUpdateMutation from "@/api/mutation/useProjectUpdateMutation";
import { Program, Project } from "@/api/query/useProjectQuery";
import PercentageCircle, {
  getPercentageCircleDefaultBandHex,
} from "@/components/shared/PercentageCircle";
import { Button } from "@/components/ui/button";
import UserAvatar from "@/components/ui/userAvatar";
import {
  Gantt,
  Task as GanntTask,
  ViewMode,
} from "@/components/views/custom/gantt/src";
import { Popover, PopoverContent, PopoverTrigger } from "@radix-ui/react-popover";
import ColorPickerIcon from "@/assets/svg/Colorpicker.svg";
import { darkenColor } from "@/components/views/custom/gantt/src/helpers/colorMixer";
import dateFormater from "@/helperFuntions/dateFormater";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";
import { useUser } from "@/hooks/useUser";
import { errorConfig } from "@/lib/toastError";
import { OrgListOfNonWorkingDaysEnum } from "@backend/src/schemas/organisationSchema";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import useProjectDuplicateMutation from "@/api/mutation/useDuplicateProject";
import { usePlanValidation } from "@/hooks/usePlan";
import TrashCan from "@/assets/svg/TrashCan.svg";
import Dialog from "@/components/common/Dialog";
import useRemoveProjectMutation from "@/api/mutation/useProjectRemoveMutation";
import TaskSubTaskForm from "@/components/tasks/taskSubTaskForm";
import { ScrollText, Settings } from "lucide-react";
import DuplicateIcon from "@/assets/svg/DuplicateIcon.svg";
import Users from "@/assets/svg/Users.svg";
import {
  PlanTypeEnumSchema,
  ProjectStatusEnumValue,
  UserRoleEnumValue,
} from "@backend/src/schemas/enums";
import calculatePortfolioAndProgramSPIs from "@/helperFuntions/projectherarch";
import { getSpiScheduleIndicatorHex } from "@/helperFuntions/spiScheduleColor";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { columnsType } from "@/components/views/custom/gantt/src/types/public-types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import useProjectManagerPortfolioDashboardQuery from "@/api/query/useProjectManagerPortfolioDashboardQuery";
import usePortfolioUpdateMutation from "@/api/mutation/useUpdatePortfolioMutation";
import useProgramUpdateMutation from "@/api/mutation/useProgramUpdateMutaion";
import useRemovePortfolioMutation from "@/api/mutation/useRemovePortfolioMutaiton";
import useRemoveProgramMutation from "@/api/mutation/useRemoveProgramMutaiton";
import {
  canDuplicateOrRemoveProject,
  canManagePortfolioHierarchyRow,
  canManageProgramHierarchyRowEnhanced,
  canManageProjectTasksAndContent,
  normalizeProjectScope,
} from "@/utils/projectRoleAccess";
import NewEditIcon from "@/assets/v2/svg/PrimaryEdit.svg";
import TrashCanV2 from "@/assets/v2/svg/trash.svg";
import CloseCircle from "@/assets/v2/svg/primary-close-circle.svg";
import CheckedSvg from "@/assets/v2/svg/tick-circle.svg";
import InputText from "@/components/common/InputText";
import DeletePortfolioProgramConfirmDialog from "@/components/project/DeletePortfolioProgramConfirmDialog";

export interface ProjectNode extends Project {
  type: "project";
  progress: number;
}

export interface ProgramNode {
  type: "program";
  programId: string;
  programName: string;
  progress: number;
  actualProgress: number;
  expectedProgress: number;
  portfolioId: string | undefined;
  projects: ProjectNode[];
  assignedUsers?: {
    programRole: string;
    user: {
      userId: string;
      email: string;
      firstName?: string;
      lastName?: string;
      avatarImg?: string;
    };
  }[];
}

// A portfolio in the final hierarchy
export interface PortfolioNode {
  type: "portfolio";
  portfolioId: string;
  portfolioName: string;
  progress: number;
  actualProgress: number;
  expectedProgress: number;
  projects: ProjectNode[]; // Projects directly under the portfolio
  programs: ProgramNode[];
  assignedUsers?: {
    portfolioRole: string;
    user: {
      userId: string;
      email: string;
      firstName?: string;
      lastName?: string;
      avatarImg?: string;
    };
  }[];
}

// The final data structure for your state is a union of these types
export type HierarchyItem = PortfolioNode | ProgramNode | ProjectNode;

function findRoadmapHierarchyGroupNode(
  hierarchyData: HierarchyItem[],
  rowId: string,
): PortfolioNode | ProgramNode | undefined {
  const flat: (PortfolioNode | ProgramNode)[] = [];
  for (const h of hierarchyData) {
    if (h.type === "portfolio") {
      flat.push(h);
      flat.push(...h.programs);
    } else if (h.type === "program") {
      flat.push(h);
    }
  }
  return flat.find(
    (node) =>
      (node.type === "portfolio" && node.portfolioId === rowId) ||
      (node.type === "program" && node.programId === rowId),
  );
}

type Props = {
  projects: Project[];
  setFilterUnit: (data: number) => void;
  FilterUnit: number;
  expandAll: boolean;
  selectedDateType: "estimated" | "actual";
  compare: "estimatedToActual" | "actualToReCalculated" | "all" | "";
  setSelectedDateType: (data: "estimated" | "actual") => void;
  isOpenWorkLoad: boolean;
  showMilestones: boolean;
  hierarchyData: HierarchyItem[];
};
type GanttTaskExtendedType = GanntTask & {
  subtask?: GanttTaskExtendedType[];
  duration: number;
  assign: any[];
  level: string;
  status: string;
  flag: string;
  baseLineTask: GanttTaskExtendedType[];
  isActive?: boolean;
};
type expandedProject = Project & { type: string };
type ProjectHierarchy = {
  portfolioId?: string;
  portfolioName?: string;
  projects: expandedProject[];
  type: string;
  programs?: program[];
};
type program = {
  programId: string;
  programName?: string;
  type: string;
  projects: expandedProject[];
};
type Calculable<T> = T & {
  __totalDuration: number;
  __weightedProgress: number;
  __weightedExpected: number;
};
function GanttView({
  projects,
  FilterUnit,
  expandAll,
  setFilterUnit,
  compare,
  selectedDateType,
  isOpenWorkLoad,
  showMilestones,
  hierarchyData,
}: Props) {
  const [expanded, setExpanded] = useState<string[]>();
  const [data, setData] = useState<Project[]>(projects);
  const removeProjectMutation = useRemoveProjectMutation();
  const { t } = useTranslation();
  const { user } = useUser();
  const [spiPortfolioAndProgram, setSpiPortfolioAndProgram] =
    useState<Record<string, number>>();

  const [showInput, setShowInput] = useState("");
  const [updateValue, setUpdateValue] = useState<{
    type: "portfolio" | "program";
    id: string;
  }>();
  const editValueRef = useRef("");

  const portfolioUpdateMutation = usePortfolioUpdateMutation();
  const programUpdateMutation = useProgramUpdateMutation();
  const removeProgramMutation = useRemoveProgramMutation();
  const removePortfolioMutation = useRemovePortfolioMutation();

  const [deletePortfolioAndProgram, setDeletePortfolioAndProgram] = useState<{
    portfolio: string;
    program: string;
  }>();

  const updatePortfolioAndProgram = () => {
    const val = editValueRef.current;
    if (updateValue && updateValue.type == "portfolio" && val) {
      portfolioUpdateMutation.mutate(
        { id: updateValue.id, value: val },
        {
          onSuccess() {
            setUpdateValue(undefined);
            editValueRef.current = "";
            setShowInput("");
            projectQuery.refetch();
          },
          onError(error) {
            toast.error(
              errorKeyFormate(error.response?.data.message ?? ""),
              errorConfig,
            );
          },
        },
      );
    } else if (
      updateValue &&
      updateValue.type == "program" &&
      val
    ) {
      programUpdateMutation.mutate(
        { id: updateValue.id, value: val },
        {
          onSuccess() {
            setUpdateValue(undefined);
            editValueRef.current = "";
            setShowInput("");
            projectQuery.refetch();
          },
          onError(error) {
            toast.error(
              errorKeyFormate(error.response?.data.message ?? ""),
              errorConfig,
            );
          },
        },
      );
    } else {
      setUpdateValue(undefined);
      setShowInput("");
    }
  };

  const handleRemovePortfolioAndProgram = () => {
    if (deletePortfolioAndProgram?.portfolio) {
      removePortfolioMutation.mutate(deletePortfolioAndProgram?.portfolio, {
        onSuccess() {
          projectQuery.refetch().then(() => {
            setDeletePortfolioAndProgram(undefined);
          });
        },
        onError(error) {
          toast.error(
            errorKeyFormate(error.response?.data.message ?? ""),
            errorConfig,
          );
        },
      });
    }
    if (deletePortfolioAndProgram?.program) {
      removeProgramMutation.mutate(deletePortfolioAndProgram?.program, {
        onSuccess() {
          projectQuery.refetch().then(() => {
            setDeletePortfolioAndProgram(undefined);
          });
        },
        onError(error) {
          toast.error(
            errorKeyFormate(error.response?.data.message ?? ""),
            errorConfig,
          );
        },
      });
    }
  };

  useEffect(() => {
    if (projects && projects.length) {
      setData(projects);
      setSpiPortfolioAndProgram(
        calculatePortfolioAndProgramSPIs(projects ?? [])
      );
    }
  }, [projects]);

  const navigate = useNavigate();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [zoomState, setZoomState] = useState(FilterUnit);
  const [view, setView] = useState(ViewMode.QuarterYear);
  const fallbackEndDate = (
    primary?: Date | string | null,
    fallback?: Date | string | null
  ): Date => {
    if (primary) return new Date(primary);
    if (fallback) return new Date(fallback);
    const date = new Date();
    date.setDate(date.getDate());
    return date;
  };
  function findLatestOverallEndDate(projects: Project[]): Date | undefined {
    let latest: Date | undefined;

    projects.forEach((project) => {
      // Estimated
      const estEnd = fallbackEndDate(
        project.estimatedEndDate,
        project.startDate
      );
      if (!latest || estEnd > latest) latest = estEnd;

      // Actual
      if (project.actualStartDate || project.actualEndDate) {
        const actStart = fallbackEndDate(
          project.actualStartDate,
          project.startDate
        );
        const actEnd = fallbackEndDate(project.actualEndDate, actStart);
        if (!latest || actEnd > latest) latest = actEnd;
      }

      // Recalculated
      if (
        project.reCalculateEndDate &&
        (project.actualStartDate || project.startDate)
      ) {
        const actStart = fallbackEndDate(
          project.actualStartDate,
          project.startDate
        );
        const recalcEnd = fallbackEndDate(project.reCalculateEndDate, actStart);
        if (!latest || recalcEnd > latest) latest = recalcEnd;
      }
    });
    return latest;
  }
  const verification = (project: Project): boolean => {
    if (!user) return false;
    return canManageProjectTasksAndContent(
      user,
      user.userOrganisation[0]?.role,
      project.assignedUsers,
      normalizeProjectScope(project),
    );
  };
  const canDuplicateOrRemoveThisProject = (project: Project): boolean => {
    if (!user) return false;
    return canDuplicateOrRemoveProject(
      user,
      user.userOrganisation[0]?.role,
      project.assignedUsers,
      normalizeProjectScope(project),
    );
  };
  const convertProject = (projectsList: Project[]): GanttTaskExtendedType[] => {
    const hierarchy = buildCompleteHierarchy(projectsList);

    // --- PERFORMANCE OPTIMIZATION 1: Pre-calculate constants and create fast lookup Sets ---
    // These are calculated only ONCE, instead of inside every call to `calculateDuration`.
    const holidaySet = new Set(
      user?.userOrganisation[0].organisation.orgHolidays?.map((h: any) =>
        dateFormater(new Date(h.holidayStartDate))
      ) ?? []
    );
    const nonWorkingDaysSet = new Set(nonWorkingDays());

    // --- PERFORMANCE OPTIMIZATION 2: Memoization cache for duration calculation ---
    // Stores results of `calculateDuration` to avoid re-calculating for the same date range.
    const durationCache = new Map<string, number>();

    const isPortfolio = (item: any): item is ProjectHierarchy =>
      "programs" in item && "projects" in item && "portfolioId" in item;
    const isProgram = (item: any): item is Program =>
      "programId" in item && "programName" in item && !("startDate" in item);
    const isProject = (item: any): item is Project =>
      "projectId" in item && "startDate" in item;

    const ensureDatesValid = (
      startDate: Date,
      endDate: Date | null
    ): { start: Date; end: Date } => {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);

      let end = endDate ? new Date(endDate) : new Date(start);
      end.setHours(0, 0, 0, 0);

      if (start instanceof Date && !isNaN(start.getTime())) {
        if (end < start || !(end instanceof Date && !isNaN(end.getTime()))) {
          end = new Date(start);
        }
      } else {
        return {
          start: new Date("invalid date"),
          end: new Date("invalid date"),
        };
      }

      return { start, end };
    };

    // --- PERFORMANCE OPTIMIZATION 3: Use .some() for early exit ---


    // --- HEAVILY OPTIMIZED FUNCTION ---
    function calculateDuration(startDate: Date, endDate: Date): number {
      const start = new Date(startDate);
      const end = new Date(endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      if (
        !(start instanceof Date && !isNaN(start.getTime())) ||
        !(end instanceof Date && !isNaN(end.getTime()))
      ) {
        return 0;
      }

      // Use cache if this exact range has been calculated before
      const cacheKey = `${start.getTime()}-${end.getTime()}`;
      if (durationCache.has(cacheKey)) {
        return durationCache.get(cacheKey)!;
      }

      let duration = 0;
      const currentDate = new Date(start);

      // Loop from start date to end date
      while (currentDate <= end) {
        const day = currentDate.getDay();
        // Use the pre-calculated Sets for O(1) average time complexity lookup.
        // This is much faster than Array.prototype.includes() which is O(n).
        if (!nonWorkingDaysSet.has(day)) {
          const formattedDate = dateFormater(currentDate);
          if (!holidaySet.has(formattedDate)) {
            duration++;
          }
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }

      if (duration === 0 && start <= end) {
        // Before returning 1, ensure the single day is a working day
        const day = start.getDay();
        if (
          !nonWorkingDaysSet.has(day) &&
          !holidaySet.has(dateFormater(start))
        ) {
          durationCache.set(cacheKey, 1);
          return 1;
        }
      }

      // Cache the result before returning
      durationCache.set(cacheKey, duration);
      return duration;
    }

    const getSmartEndDateAndDuration = (
      item: Project,
      dateType: "actual" | "estimated"
    ): { actualStartDay: Date; actualLastDay: Date; duration: number } => {
      const start =
        dateType === "actual" && item.actualStartDate
          ? new Date(item.actualStartDate)
          : new Date(item.startDate);

      const potentialEnd = fallbackEndDate(
        dateType === "actual" ? item.actualEndDate : item.estimatedEndDate,
        start
      );

      const { start: actualStartDay, end: actualLastDay } = ensureDatesValid(
        start,
        potentialEnd
      );

      const duration =
        dateType === "actual"
          ? item.actualDuration ??
          item.estimatedDuration ??
          calculateDuration(actualStartDay, actualLastDay)
          : item.estimatedDuration ??
          item.actualDuration ??
          calculateDuration(actualStartDay, actualLastDay);

      return { actualStartDay, actualLastDay, duration };
    };

    // --- PERFORMANCE OPTIMIZATION 4: More efficient min/max date calculation ---
    const getMinMaxDatesFromTasks = (
      tasks: GanttTaskExtendedType[]
    ): { start?: Date; end?: Date } => {
      // Using timestamps avoids creating many new Date objects inside the loop.
      let minTime = Infinity;
      let maxTime = -Infinity;
      let validDatesFound = false;

      for (const t of tasks) {
        if (t.start) {
          const startTime = t.start.getTime();
          if (!isNaN(startTime)) {
            minTime = Math.min(minTime, startTime);
            validDatesFound = true;
          }
        }
        if (t.end) {
          // The 'end' property is the day *after* the last day.
          // Subtracting 1ms is a safe way to get the timestamp of the actual last day.
          const endTime = t.end.getTime() - 1;
          if (!isNaN(endTime)) {
            maxTime = Math.max(maxTime, endTime);
            validDatesFound = true;
          }
        }
      }

      if (!validDatesFound) return {};

      return { start: new Date(minTime), end: new Date(maxTime) };
    };

    const getMinMaxDates = (dates: Date[]): { start?: Date; end?: Date } => {
      // This version avoids filtering and mapping twice for min and max.
      let minTime = Infinity;
      let maxTime = -Infinity;

      for (const d of dates) {
        const time = d.getTime();
        if (!isNaN(time)) {
          minTime = Math.min(minTime, time);
          maxTime = Math.max(maxTime, time);
        }
      }

      if (minTime === Infinity) return {};

      return { start: new Date(minTime), end: new Date(maxTime) };
    };

    // The rest of the `buildTasks` function and its internal logic remains unchanged.
    // It will automatically benefit from the faster helper functions defined above.
    const buildTasks = (
      item: ProjectHierarchy | Program | Project,
      parentId = "",
      parentLevel = ""
    ): GanttTaskExtendedType[] => {
      // ... no changes to the internal logic of this function ...
      // All calls to `calculateDuration`, `getMinMaxDates`, etc., will now use the optimized versions.
      const isPortfolioItem = isPortfolio(item);
      const isProgramItem = isProgram(item);
      const isProjectItem = isProject(item);
      const id = isPortfolioItem
        ? item.portfolioId
        : isProgramItem
          ? item.programId
          : item.projectId;
      const name = isPortfolioItem
        ? item.portfolioName
        : isProgramItem
          ? item.programName
          : item.projectName;

      const subtasks: GanttTaskExtendedType[] = [];

      // Build subtasks recursively first
      if (isPortfolioItem) {
        item.programs?.forEach((program) => {
          subtasks.push(
            ...buildTasks(program, id, `${parentLevel}.${subtasks.length + 1}`)
          );
        });
        item.projects?.forEach((project) => {
          subtasks.push(
            ...buildTasks(project, id, `${parentLevel}.${subtasks.length + 1}`)
          );
        });
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error: projects might not exist on Program type formally but used in hierarchy
      } else if (isProgramItem && item.projects?.length) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        item.projects.forEach((project) => {
          subtasks.push(
            ...buildTasks(project, id, `${parentLevel}.${subtasks.length + 1}`)
          );
        });
      }

      const baseColor =
        isPortfolioItem || isProgramItem ? "#525DF9" : (item.ganttColor || "#22c55e");

      // Helper to create a Gantt task object
      const createTask = (
        taskId: string,
        label: string,
        actualStartDay: Date, // Actual first day of task
        actualLastDay: Date, // Actual last day of task
        highlightColor: string,
        isComparison = false,
        duration?: number // Optional: pass calculated duration
      ): GanttTaskExtendedType => {
        // Ensure dates are valid before creating task
        if (
          !(
            actualStartDay instanceof Date && !isNaN(actualStartDay.getTime())
          ) ||
          !(actualLastDay instanceof Date && !isNaN(actualLastDay.getTime()))
        ) {
          console.warn(
            `Skipping task ${taskId}: Invalid dates (Start: ${actualStartDay}, End: ${actualLastDay})`
          );
          return null as any; // Return null or similar to indicate skip
        }

        const ganttEndDate = new Date(actualLastDay);
        ganttEndDate.setDate(ganttEndDate.getDate() + 1); // Add 1 day for Gantt library 'end' format
        ganttEndDate.setHours(0, 0, 0, 0); // Add 1 day for Gantt library 'end' format
        actualStartDay.setHours(0, 0, 0, 0);
        const calculatedDuration =
          duration ?? calculateDuration(actualStartDay, actualLastDay); // Calculate if not provided
        function calculateProgress(isProjectItem: boolean, taskId: string, item: any) {
          // Safe number parser — always returns a valid number
          const toNum = (val: any, fallback = 0) => {
            const num = parseFloat(val);
            return isFinite(num) ? num : fallback;
          };

          if (!isProjectItem) {
            return toNum(item?.progress, 0);
          }

          const progression = toNum(item?.progressionPercentage, 1); // fallback to 1 to avoid NaN/0
          const spi = toNum(item?.spi, 1);

          // Handle "-est" case
          if (taskId?.endsWith("-est")) {
            return progression !== 0 ? toNum((progression * 100) / (spi / 100), 0) : 0;
          }

          return toNum(progression * 100, 0);
        }

        const progress = calculateProgress(isProjectItem, taskId, item);


        return {
          createdByUserId: "", // Assuming default
          id: taskId,
          name: isComparison
            ? `${label} (${taskId.endsWith("-est")
              ? t("estimated_tooltip")
              : taskId.endsWith("-act")
                ? t("Current") // Assuming 'Current' for actual
                : taskId.endsWith("-recalc")
                  ? t("Recalculated")
                  : "Comparison" // Fallback label
            })`
            : `${label} ${taskId.endsWith("-est")
              ? `(${t("estimated_tooltip")})`
              : taskId.endsWith("-act")
                ? `(${t("Current")})` // Assuming 'Current' for actual
                : taskId.endsWith("-recalc")
                  ? `(${t("Recalculated")})`
                  : "" // Fallback label
            }`,
          type:
            isPortfolioItem || isProgramItem // Parent tasks type is 'project' for rollup visually
              ? "project"
              : item.isMilestone // Project item types
                ? "milestone"
                : "task",
          start: actualStartDay, // Convert actual start day to UTC
          end: ganttEndDate, // Convert day *after* last day to UTC
          duration: calculatedDuration,
          progress: progress, // Assuming progressionPercentage is a number between 0 and 1
          showProgress: isProjectItem, // Only show progress bar for projects
          project: parentId, // Parent relationship
          dependencies: [], // Assuming no dependencies in this conversion
          assign: isProjectItem // Only projects have assigned users directly
            ? item.assignedUsers?.map((u) => ({
              user: u.user,
              role: u.projectRole,
            })) // Assuming this structure
            : ([] as any),
          subtask: undefined, // Will be set later for parent tasks
          level: "", // Assuming level is set elsewhere or not used here
          status: isProjectItem ? item.status ?? "" : "", // Only projects have status
          flag: "", // Assuming no flags
          hideChildren:
            isPortfolioItem || isProgramItem
              ? !expanded?.includes(taskId)
              : false, // Hide children for expanded parents
          isDisabled: isProjectItem
            ? !(verification(item) && item?.isActive)
            : true,
          isActive: isProjectItem ? item.isActive : true,
          baseLineTask: [], // Will be set later
          styles: {
            // Using the base color and darkening for specific states
            backgroundColor: isProjectItem && item.isActive === false ? "#d1d5db" : highlightColor, // Use gray if inactive
            backgroundSelectedColor: isProjectItem && item.isActive === false ? "#9ca3af" : darkenColor(highlightColor, 0.2),
            progressColor: isProjectItem && item.isActive === false ? "#9ca3af" : darkenColor(highlightColor, 0.15),
            progressSelectedColor: isProjectItem && item.isActive === false ? "#6b7280" : darkenColor(highlightColor, 0.3),
          },
          milestones: showMilestones && isProjectItem && item.tasks
            ? item.tasks
              .filter((t) => t.milestoneIndicator === true)
              .map((t) => ({
                id: t.taskId,
                name: t.taskName,
                start: new Date(t.startDate),
                color: "#ef4444", // Red color to match project tasks view
              }))
            : undefined,
        };
      };

      const taskList: GanttTaskExtendedType[] = [];

      // --- Handle Project Items (Leaf Nodes in this hierarchy) ---
      if (isProjectItem) {
        let mainStart: Date; // Actual first day
        let mainEndActualLastDay: Date; // Actual last day
        let mainDuration: number | undefined;

        const baseLines: GanttTaskExtendedType[] = [];

        // Determine main bar dates and create baseline tasks for the project
        switch (compare) {
          case "estimatedToActual":
            // Main task = estimated range
            // eslint-disable-next-line no-case-declarations
            const smartEst = getSmartEndDateAndDuration(item, "estimated");
            mainStart = smartEst.actualStartDay;
            mainEndActualLastDay = smartEst.actualLastDay;
            mainDuration = smartEst.duration;

            // Add Actual baseline
            // eslint-disable-next-line no-case-declarations
            const smartAct = getSmartEndDateAndDuration(item, "actual");
            // eslint-disable-next-line no-case-declarations
            const actualBaseline = createTask(
              `${id}-act`,
              name ?? "",
              smartAct.actualStartDay,
              smartAct.actualLastDay,
              "#FF69B4", // Pink for Actual
              true,
              smartAct.duration
            );
            if (actualBaseline) baseLines.push(actualBaseline);
            break;

          case "actualToReCalculated":
            // Main task = actual range
            const smartActMain = getSmartEndDateAndDuration(item, "actual");
            mainStart = smartActMain.actualStartDay;
            mainEndActualLastDay = smartActMain.actualLastDay;
            mainDuration = smartActMain.duration;

            // Determine Recalculated baseline dates
            const recalcBaseDate = item.actualStartDate
              ? new Date(item.actualStartDate)
              : new Date(item.startDate); // Base start from actual or project start
            const { start: recalcStart, end: recalcEndActualLastDay } =
              ensureDatesValid(
                recalcBaseDate,
                item.reCalculateEndDate
                  ? new Date(item.reCalculateEndDate)
                  : null // Use recalc end if exists, otherwise potentially invalid
              );

            const recalculatedBaseline = createTask(
              `${id}-recalc`,
              name ?? "",
              recalcStart,
              recalcEndActualLastDay,
              "#525DF9", // Blue for Recalculated
              true,
              calculateDuration(recalcStart, recalcEndActualLastDay)
            );
            if (recalculatedBaseline) baseLines.push(recalculatedBaseline);
            break;

          case "all":
            // Main task = estimated range
            const smartEstAll = getSmartEndDateAndDuration(item, "estimated");
            mainStart = smartEstAll.actualStartDay;
            mainEndActualLastDay = smartEstAll.actualLastDay;
            mainDuration = smartEstAll.duration;

            // Add Actual baseline
            const smartActAll = getSmartEndDateAndDuration(item, "actual");
            const actualBaselineAll = createTask(
              `${id}-act`,
              name ?? "",
              smartActAll.actualStartDay,
              smartActAll.actualLastDay,
              "#FF69B4", // Pink for Actual
              true,
              smartActAll.duration
            );
            if (actualBaselineAll) baseLines.push(actualBaselineAll);

            // Add Recalculated baseline if exists, otherwise fallback logic similar to actualToReCalculated case
            const recalcBaseDateAll = item.actualStartDate
              ? new Date(item.actualStartDate)
              : new Date(item.startDate);
            const { start: recalcStartAll, end: recalcEndActualLastDayAll } =
              ensureDatesValid(
                recalcBaseDateAll,
                item.reCalculateEndDate
                  ? new Date(item.reCalculateEndDate)
                  : null // Use recalc end if exists
              );
            // eslint-disable-next-line no-case-declarations
            const recalculatedBaselineAll = createTask(
              `${id}-recalc`,
              name ?? "",
              recalcStartAll,
              recalcEndActualLastDayAll,
              "#525DF9", // Blue for Recalculated
              true,
              calculateDuration(recalcStartAll, recalcEndActualLastDayAll)
            );
            if (recalculatedBaselineAll)
              baseLines.push(recalculatedBaselineAll);

            break;

          default: // "none" or any other value
            // Main task = selectedDateType range
            // eslint-disable-next-line no-case-declarations
            const smartDefault = getSmartEndDateAndDuration(
              item,
              selectedDateType
            );
            mainStart = smartDefault.actualStartDay;
            mainEndActualLastDay = smartDefault.actualLastDay;
            mainDuration = smartDefault.duration;
            break;
        }

        // Ensure main task dates are valid before creating
        const { start: finalMainStart, end: finalMainEndActualLastDay } =
          ensureDatesValid(mainStart, mainEndActualLastDay);

        // Create the main task for the project item
        const mainTask = createTask(
          `${selectedDateType === "estimated" ? `${id}-est` : `${id}-act`}`,
          name ?? "",
          finalMainStart,
          finalMainEndActualLastDay,
          selectedDateType == "actual" && !compare ? "#FF69B4" : compare == "actualToReCalculated" ? "#FF69B4" : baseColor, // Use pink for actual main bar when no comparison
          false, // This is the main bar
          mainDuration
        );

        if (mainTask) {
          // Only push if main task was successfully created (dates were valid)
          mainTask.baseLineTask = baseLines; // Assign baselines to the main task
          taskList.push(mainTask);
        }
      } else {
        // --- Handle Non-Project Items (Portfolio or Program Parents) ---

        const parentBaseLines: GanttTaskExtendedType[] = [];

        // Aggregate dates from children's tasks to create parent baselines
        if (subtasks.length > 0 && compare) {
          // Filter children's tasks by type (main bar vs baseline)
          // Note: BaseLineTask array contains the comparison bars for projects.
          // We need to collect all actual and recalculated baseline tasks from children's baseLineTask arrays.
          const allChildActualBaselines: GanttTaskExtendedType[] = [];
          const allChildRecalculatedBaselines: GanttTaskExtendedType[] = [];
          const allChildMainTasks: GanttTaskExtendedType[] = [];

          subtasks.forEach((childTask) => {
            if (isProject(childTask)) {
              // Process only project type children for baselines
              allChildMainTasks.push(childTask); // Add project's main bar
              if (Array.isArray(childTask.baseLineTask)) {
                (childTask.baseLineTask as GanttTaskExtendedType[]).forEach(
                  (childBaseline) => {
                    if (childBaseline.id.endsWith("-act")) {
                      allChildActualBaselines.push(childBaseline);
                    } else if (childBaseline.id.endsWith("-recalc")) {
                      allChildRecalculatedBaselines.push(childBaseline);
                    }
                    // Note: Estimated baselines are typically the main bar when needed for comparison start point
                  }
                );
              }
            } else {
              // If the child is a parent task (Program/Portfolio), include its own baselines
              // in the aggregation for this higher-level parent.
              allChildMainTasks.push(childTask); // Add child parent's main bar
              if (Array.isArray(childTask.baseLineTask)) {
                (childTask.baseLineTask as GanttTaskExtendedType[]).forEach(
                  (childBaseline) => {
                    if (childBaseline.id.endsWith("-act")) {
                      allChildActualBaselines.push(childBaseline);
                    } else if (childBaseline.id.endsWith("-recalc")) {
                      allChildRecalculatedBaselines.push(childBaseline);
                    }
                  }
                );
              }
            }
          });

          // Aggregate dates from these filtered lists
          // const { start: minMainStart, end: maxMainEnd } = getMinMaxDatesFromTasks(allChildMainTasks); // Range of children's main bars
          const { start: minActStart, end: maxActEnd } =
            getMinMaxDatesFromTasks(allChildActualBaselines); // Range of children's actual baselines
          const { start: minRecalcStart, end: maxRecalcEnd } =
            getMinMaxDatesFromTasks(allChildRecalculatedBaselines); // Range of children's recalculated baselines

          // Helper to create a parent baseline task
          const createParentBaseline = (
            prefix: string,
            start?: Date, // Actual first day of the range
            end?: Date, // Actual last day of the range
            color = baseColor
          ): GanttTaskExtendedType | null => {
            // Ensure dates are valid for a baseline task
            const { start: actualStartDay, end: actualLastDay } =
              ensureDatesValid(
                start ?? new Date("invalid date"), // Provide invalid date if start is missing
                end ?? new Date("invalid date") // Provide invalid date if end is missing
              );

            if (
              !(
                actualStartDay instanceof Date &&
                !isNaN(actualStartDay.getTime())
              ) ||
              !(
                actualLastDay instanceof Date && !isNaN(actualLastDay.getTime())
              )
            ) {
              // console.warn(`Skipping parent baseline ${id}-${prefix}: Invalid aggregated dates`);
              return null; // Skip creating the baseline if the aggregated range is invalid
            }

            const duration = calculateDuration(actualStartDay, actualLastDay);

            return createTask(
              `${id}-${prefix}`,
              name ?? "", // Use parent's name
              actualStartDay, // Actual first day
              actualLastDay, // Actual last day
              color,
              true, // It's a comparison task
              duration
            );
          };

          // Create parent baselines based on the comparison mode and aggregated child dates
          switch (compare) {
            case "estimatedToActual":
              // Parent main bar will represent the range of children's estimated/main bars (minMainStart/maxMainEnd)
              // Add Parent Actual baseline
              const actualParentBaseline = createParentBaseline(
                "act",
                minActStart,
                maxActEnd,
                "#FF69B4"
              );
              if (actualParentBaseline)
                parentBaseLines.push(actualParentBaseline);
              break;

            case "actualToReCalculated":
              // Parent main bar will represent the range of children's actual bars (minMainStart/maxMainEnd)
              // Add Parent Recalculated baseline
              const recalculatedParentBaseline = createParentBaseline(
                "recalc",
                minRecalcStart,
                maxRecalcEnd,
                "#22c55e"
              );
              if (recalculatedParentBaseline)
                parentBaseLines.push(recalculatedParentBaseline);
              break;

            case "all":
              // Parent main bar will represent the range of children's estimated/main bars (minMainStart/maxMainEnd)
              // Add Parent Actual and Recalculated baselines
              const actualParentBaselineAll = createParentBaseline(
                "act",
                minActStart,
                maxActEnd,
                "#FF69B4"
              );
              if (actualParentBaselineAll)
                parentBaseLines.push(actualParentBaselineAll);
              const recalculatedParentBaselineAll = createParentBaseline(
                "recalc",
                minRecalcStart,
                maxRecalcEnd,
                "#22c55e"
              );
              if (recalculatedParentBaselineAll)
                parentBaseLines.push(recalculatedParentBaselineAll);
              // Note: We don't create an 'estimated' baseline for the parent when the main bar is estimated,
              // following the pattern of the project level.
              break;

            // default: "none" comparison is handled by parentBaseLines being empty initially
          }
        }

        // Calculate start/end for the main parent task
        // This task spans the entire range of its children's *main* bars and their baselines
        const allDates: Date[] = [];
        subtasks.forEach((t) => {
          if (t.start) {
            const start = new Date(t.start);
            if (start instanceof Date && !isNaN(start.getTime()))
              allDates.push(start);
          }
          // Need the actual last day from the task's end property for aggregation
          if (t.end) {
            const actualLastDay = new Date(t.end);
            actualLastDay.setDate(actualLastDay.getDate() - 1); // Subtract 1 day
            if (
              actualLastDay instanceof Date &&
              !isNaN(actualLastDay.getTime())
            )
              allDates.push(actualLastDay);
          }

          // Include dates from children's baselines too for the overall parent span
          if (Array.isArray(t.baseLineTask)) {
            t.baseLineTask.forEach((b) => {
              if (b.start) {
                const start = new Date(b.start);
                if (start instanceof Date && !isNaN(start.getTime()))
                  allDates.push(start);
              }
              if (b.end) {
                const actualLastDay = new Date(b.end);
                actualLastDay.setDate(actualLastDay.getDate() - 1); // Subtract 1 day
                if (
                  actualLastDay instanceof Date &&
                  !isNaN(actualLastDay.getTime())
                )
                  allDates.push(actualLastDay);
              }
            });
          }
        });

        // Get the overall min/max date range for the parent main bar
        const { start: overallMinStart, end: overallMaxEnd } =
          getMinMaxDates(allDates);

        // Ensure the overall range is valid before creating the task
        const { start: parentStartDay, end: parentEndActualLastDay } =
          ensureDatesValid(
            overallMinStart ?? new Date("invalid date"), // Use minStart if available, otherwise invalid
            overallMaxEnd ?? null // maxEnd can be null if allDates is empty
          );

        // Calculate duration for the main parent bar
        const parentDuration = calculateDuration(
          parentStartDay,
          parentEndActualLastDay
        );

        // Create the main parent task (rollup)
        const parentTask = createTask(
          id ?? "",
          name ?? "",
          parentStartDay, // Actual first day of the parent span
          parentEndActualLastDay, // Actual last day of the parent span
          baseColor, // Use the parent's base color for the main bar
          false, // It's the main bar
          parentDuration
        );

        if (parentTask) {
          // Only push if parent task was successfully created (dates were valid)
          if (subtasks.length) parentTask.subtask = subtasks; // Assign children as subtasks
          parentTask.baseLineTask = parentBaseLines; // Assign the calculated parent baselines
          taskList.push(parentTask);
        }
      } // End of non-Project Item handling

      return taskList; // Return the list of tasks created at this level
    }; // End of buildTasks function

    const builtTasks = hierarchy.flatMap((h, index) => {
      const tasks = buildTasks(h, "", `${index + 1}`);
      return tasks.filter((task) => task !== null);
    });

    const finalTasks = sortAndFlattenTask(builtTasks);

    return finalTasks;
  };

  const sortAndFlattenTask = (
    tasks: GanttTaskExtendedType[],
    parentLevel = ""
  ): GanttTaskExtendedType[] => {
    let result: GanttTaskExtendedType[] = [];

    tasks.forEach((task, index) => {
      const level = parentLevel
        ? `${parentLevel}.${index + 1}`
        : `${index + 1}`;
      result.push({ ...task, level });

      if (task.subtask?.length) {
        result = result.concat(sortAndFlattenTask(task.subtask, level));
      }
    });

    return result;
  };

  const nonWorkingDays = useCallback(() => {
    const nWd =
      user?.userOrganisation.map((org) => org.organisation.nonWorkingDays) ||
      [];
    const mapDayToNumber = (day: OrgListOfNonWorkingDaysEnum) => {
      switch (day) {
        case "SUN":
          return 0;
        case "MON":
          return 1;
        case "TUE":
          return 2;
        case "WED":
          return 3;
        case "THU":
          return 4;
        case "FRI":
          return 5;
        case "SAT":
          return 6;
        default:
          return 0;
      }
    };
    const result: number[] = [];
    nWd.forEach((days) => {
      days.forEach((day) => {
        result.push(mapDayToNumber(day));
      });
    });
    return result;
  }, [user]);

  const UTCDateConverter = useCallback((date: Date) => {
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
    );
  }, []);

  // Using your existing duration calculation for business logic consistency
  const calculateDurations = useCallback(
    (startDate: Date, endDate: Date): number => {
      const start = UTCDateConverter(new Date(startDate));
      const end = UTCDateConverter(new Date(endDate));

      end.setDate(end.getDate() + 1);
      const excludedDays = nonWorkingDays();
      const holidays =
        user?.userOrganisation?.[0]?.organisation?.orgHolidays?.map((h) =>
          dateFormater(new Date(h.holidayStartDate))
        ) ?? [];

      let duration = 0;
      for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
        const day = d.getDay();
        const formattedDate = d.toISOString().split("T")[0];
        if (
          !excludedDays.includes(day) &&
          !holidays.includes(dateFormater(new Date(formattedDate)))
        ) {
          duration++;
        }
      }
      return duration;
    },
    [user, nonWorkingDays]
  );

  /**
   * Calculates progress metrics for a single project using the component's duration logic.
   */
  const calculateProjectMetrics = useCallback(
    (project: Project) => {
      const { actualStartDate, actualEndDate, progressionPercentage } = project;
      const expectedRaw = Number((project as any).expectedprogress || 0);

      if (!actualStartDate || !actualEndDate) {
        return { duration: 0, weightedProgress: 0, progress: 0, expectedProgress: expectedRaw, weightedExpected: 0 };
      }

      const duration = calculateDurations(
        new Date(actualStartDate),
        new Date(actualEndDate)
      );
      const progress = Math.round(Number(progressionPercentage || 0) * 100);
      const weightedProgress = progress * duration;
      const weightedExpected = expectedRaw * duration;

      return { duration, weightedProgress, progress, expectedProgress: expectedRaw, weightedExpected };
    },
    [calculateDurations]
  );

  /**
   * Builds a complete project hierarchy including portfolios, orphan programs,
   * and standalone projects, with weighted progress calculated at each level.
   */
  const buildCompleteHierarchy = useCallback(
    (projectsList: Project[]): HierarchyItem[] => {
      const portfolioMap = new Map<string, Calculable<PortfolioNode>>();
      const orphanProgramMap = new Map<string, Calculable<ProgramNode>>();
      const standaloneProjects: ProjectNode[] = [];

      for (const project of projectsList) {
        if (!project.projectId) continue;

        const { duration, weightedProgress, progress, weightedExpected } =
          calculateProjectMetrics(project);
        const projectNode: ProjectNode = {
          ...project,
          type: "project",
          progress,
        };

        const hasPortfolio = !!project.portfolio?.portfolioId;
        const hasProgram = !!project.program?.programId;

        if (hasPortfolio) {
          const portfolioId = project.portfolio!.portfolioId;
          if (!portfolioMap.has(portfolioId)) {
            portfolioMap.set(portfolioId, {
              type: "portfolio",
              portfolioId,
              portfolioName:
                project.portfolio!.portfolioName || "Unnamed Portfolio",
              progress: 0,
              actualProgress: 0,
              expectedProgress: 0,
              projects: [],
              programs: [],
              assignedUsers: (project as any).portfolio?.assignedUsers,
              __totalDuration: 0,
              __weightedProgress: 0,
              __weightedExpected: 0,
            });
          }
          const portfolioEntry = portfolioMap.get(portfolioId)!;
          portfolioEntry.__totalDuration += duration;
          portfolioEntry.__weightedProgress += weightedProgress;
          portfolioEntry.__weightedExpected += weightedExpected;

          if (hasProgram) {
            const programId = project.program!.programId;
            let programEntry = portfolioEntry.programs.find(
              (p) => p.programId === programId
            ) as Calculable<ProgramNode> | undefined;

            if (!programEntry) {
              programEntry = {
                type: "program",
                programId,
                programName: project.program!.programName || "Unnamed Program",
                progress: 0,
                actualProgress: 0,
                expectedProgress: 0,
                portfolioId: portfolioEntry.portfolioId,
                projects: [],
                assignedUsers: (project as any).program?.assignedUsers,
                __totalDuration: 0,
                __weightedProgress: 0,
                __weightedExpected: 0,
              };
              portfolioEntry.programs.push(programEntry as ProgramNode);
            }

            programEntry.__totalDuration += duration;
            programEntry.__weightedProgress += weightedProgress;
            programEntry.__weightedExpected += weightedExpected;
            programEntry.projects.push(projectNode);
          } else {
            portfolioEntry.projects.push(projectNode);
          }
        } else if (hasProgram) {
          const programId = project.program!.programId;
          if (!orphanProgramMap.has(programId)) {
            orphanProgramMap.set(programId, {
              type: "program",
              programId,
              programName: project.program!.programName || "Unnamed Program",
              progress: 0,
              actualProgress: 0,
              expectedProgress: 0,
              portfolioId: undefined,
              projects: [],
              assignedUsers: (project as any).program?.assignedUsers,
              __totalDuration: 0,
              __weightedProgress: 0,
              __weightedExpected: 0,
            });
          }
          const programEntry = orphanProgramMap.get(programId)!;
          programEntry.__totalDuration += duration;
          programEntry.__weightedProgress += weightedProgress;
          programEntry.__weightedExpected += weightedExpected;
          programEntry.projects.push(projectNode);
        } else {
          standaloneProjects.push(projectNode);
        }
      }

      const finalPortfolios = Array.from(portfolioMap.values())?.map((p) => {
        p.programs.forEach((prog) => {
          const program = prog as Calculable<ProgramNode>;
          program.progress =
            program.__totalDuration > 0
              ? Math.round(program.__weightedProgress / program.__totalDuration)
              : 0;
          program.actualProgress = program.progress;
          program.expectedProgress =
            program.__totalDuration > 0
              ? Math.round(program.__weightedExpected / program.__totalDuration)
              : 0;
          delete (program as any).__totalDuration;
          delete (program as any).__weightedProgress;
          delete (program as any).__weightedExpected;
        });
        p.progress =
          p.__totalDuration > 0
            ? Math.round(p.__weightedProgress / p.__totalDuration)
            : 0;
        p.actualProgress = p.progress;
        p.expectedProgress =
          p.__totalDuration > 0
            ? Math.round(p.__weightedExpected / p.__totalDuration)
            : 0;
        delete (p as any).__totalDuration;
        delete (p as any).__weightedProgress;
        delete (p as any).__weightedExpected;
        return p;
      });

      const finalOrphanPrograms = Array.from(orphanProgramMap.values())?.map(
        (p) => {
          p.progress =
            p.__totalDuration > 0
              ? Math.round(p.__weightedProgress / p.__totalDuration)
              : 0;
          p.actualProgress = p.progress;
          p.expectedProgress =
            p.__totalDuration > 0
              ? Math.round(p.__weightedExpected / p.__totalDuration)
              : 0;
          delete (p as any).__totalDuration;
          delete (p as any).__weightedProgress;
          delete (p as any).__weightedExpected;
          return p;
        }
      );

      return [
        ...finalPortfolios,
        ...finalOrphanPrograms,
        ...standaloneProjects,
      ];
    },
    [calculateProjectMetrics]
  );

  // 3. UPDATED useEffect TO USE THE NEW LOGIC

  const expandToggle = (id: string) => {
    if (expanded?.includes(id)) {
      setExpanded(expanded.filter((t) => t !== id));
    } else {
      setExpanded([...(expanded ?? []), id]);
    }
  };
  const Divref = useRef<HTMLDivElement | null>(null);
  const GanttHeight = useRef<number>(600);
  const ganttPdfRef = useRef<HTMLDivElement | null>(null);

  const setGanttHeight = (num: number) => {
    GanttHeight.current = num;
  }
  const updateHeight = () => {
    setGanttHeight(
      Divref.current?.offsetHeight ? Divref.current.offsetHeight : 600
    );
  };
  useEffect(() => {
    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (Divref.current) {
      resizeObserver.observe(Divref.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [Divref.current?.offsetHeight]);

  const viewModes = [
    ViewMode.Day,
    ViewMode.Week,
    ViewMode.Month,
    ViewMode.QuarterYear,
    ViewMode.Year,
  ];
  const minZoom = 0;
  const maxZoom = viewModes.length - 1;

  const zoominout = (num: number) => {
    // Ensure zoom level is within bounds
    const newZoomState = Math.max(minZoom, Math.min(num, maxZoom));
    setZoomState(newZoomState);
    setView(viewModes[newZoomState]);
  };

  const [ganttDate, setGanttDate] = useState({
    start: new Date(),
    end: new Date(),
  });

  useEffect(() => {
    const project = projects.sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );
    setGanttDate({
      start: UTCDateConverter(new Date(project[0].startDate)),
      end: UTCDateConverter(
        new Date(project[project.length - 1].actualEndDate)
      ),
    });
  }, [projects]);

  useEffect(() => {
    zoominout(FilterUnit);
  }, [FilterUnit]);
  useEffect(() => {
    setFilterUnit(zoomState);
  }, [zoomState]);
  useEffect(() => {
    let timeoutId: number;
    const handleScroll = (event: WheelEvent) => {
      if (event.ctrlKey) {
        event.preventDefault();
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (event.deltaY < 0 && zoomState > minZoom) {
            zoominout(zoomState - 1);
            // Decrement zoom
          } else if (event.deltaY > 0 && zoomState < maxZoom) {
            zoominout(zoomState + 1); // Increment zoom
          }
        }, 100); // Adjust debounce delay as needed
      }
    };

    const div = Divref.current;
    if (div) {
      div.addEventListener("wheel", handleScroll);
    }

    return () => {
      if (div) {
        div.removeEventListener("wheel", handleScroll);
      }
      clearTimeout(timeoutId);
    };
  }, [zoomState, viewModes, minZoom, maxZoom]);
  const projectQuery = useProjectManagerPortfolioDashboardQuery({ portfolioId: "", programId: "" }, false);
  const projectUpdateMutation = useProjectUpdateMutation("");
  const projectColorUpdateMutation = useProjectUpdateMutation("", false);
  const dataRef = useRef(data);
  dataRef.current = data;
  const lastDragUpdateRef = useRef<{ key: string; at: number } | null>(null);

  const projectUpdate = useCallback((updatedData: GanttTaskExtendedType) => {
    const newStartDate = UTCDateConverter(updatedData.start);
    // Gantt `end` is exclusive (day after last day); DB `estimatedEndDate` is inclusive.
    const newEstimatedEnd = UTCDateConverter(updatedData.end);
    newEstimatedEnd.setUTCDate(newEstimatedEnd.getUTCDate() - 1);

    const rawId = updatedData.id
      .replace(/-est$/, "")
      .replace(/-act$/, "")
      .replace(/-recalc$/, "");

    const current = dataRef.current.find((p) => p.projectId === rawId);
    if (!current) return;

    const toUtcDay = (date: Date) => UTCDateConverter(date).getTime();
    const currentStart = new Date(current.startDate);
    const currentEnd = current.estimatedEndDate
      ? new Date(current.estimatedEndDate)
      : currentStart;

    const isStartDateUpdated = toUtcDay(newStartDate) !== toUtcDay(currentStart);
    const isEndDateUpdated =
      toUtcDay(newEstimatedEnd) !== toUtcDay(currentEnd);

    if (!isStartDateUpdated && !isEndDateUpdated) return;

    const dedupeKey = `${rawId}|${toUtcDay(newStartDate)}|${toUtcDay(newEstimatedEnd)}`;
    const now = Date.now();
    if (
      lastDragUpdateRef.current?.key === dedupeKey &&
      now - lastDragUpdateRef.current.at < 1000
    ) {
      return;
    }
    lastDragUpdateRef.current = { key: dedupeKey, at: now };

    setData((prev) =>
      prev.map((project) =>
        project.projectId === rawId
          ? {
            ...project,
            startDate: newStartDate.toISOString(),
            estimatedEndDate: newEstimatedEnd.toISOString(),
          }
          : project
      )
    );

    projectUpdateMutation.mutate(
      {
        projectId: rawId,
        startDate: newStartDate,
        estimatedEndDate: newEstimatedEnd,
      },
      {
        onError(error) {
          lastDragUpdateRef.current = null;
          const err = error.response?.data;
          toast.error(
            errorKeyFormate(err?.message ?? "Unknown error"),
            errorConfig
          );
        },
      }
    );
  }, [UTCDateConverter, projectUpdateMutation]);

  const handleProjectDateChange = useCallback(
    (task: GanntTask) => projectUpdate(task as GanttTaskExtendedType),
    [projectUpdate],
  );
  const [convertedTasks, setConvertedTasks] = useState<GanttTaskExtendedType[]>(
    []
  );
  useMemo(
    () => setConvertedTasks(convertProject(data)),
    [data, expanded, selectedDateType, compare, showMilestones]
  );

  /** Taller rows when comparing schedules OR when milestones + captions are shown (compare off). barFill is reduced so taskHeight stays ~30px. */
  const ganttRoadmapRowLayout = useMemo(() => {
    const defaultRowHeight = 50;
    const defaultBarFill = 60;
    const targetBarThicknessPx = 30;
    if (!compare) {
      return { rowHeight: defaultRowHeight, barFill: defaultBarFill };
    }
    const rowHeight = compare === "all" ? 75 : 65;
    const barFill = Math.round((targetBarThicknessPx / rowHeight) * 100);
    return { rowHeight, barFill };
  }, [compare]);

  useEffect(() => {
    const tempV: string[] = [];

    if (expandAll) {
      convertedTasks.forEach((task) => {
        if (task.type === "project") {
          tempV.push(task.id);
        }
      });
    }
    const hasChanged =
      expanded?.length !== tempV.length ||
      !expanded?.every((id, index) => id === tempV[index]);
    if (hasChanged) {
      setExpanded(tempV);
    }
  }, [expandAll]);

  const projectDuplicateMutation = useProjectDuplicateMutation();
  const { validatePlan } = usePlanValidation();
  const [projectId, setProjectId] = useState<string>();
  const [remove, setRemove] = useState<string>();
  const removeProject = () => {
    removeProjectMutation.mutate(remove ?? "", {
      onSuccess(data) {
        toast.success(data.data.message);
        projectQuery.refetch();
        setRemove(undefined);
      },
      onError(error) {
        toast.error(errorKeyFormate(error.response?.data.message + ""));
      },
    });
  };

  const contextmanu: React.FC<{
    task: GanntTask;
    fontSize: string;
    fontFamily: string;
    userId?: string;
    onEventCall: (data: { action: string; data: GanntTask; }) => void;
    onColorChange: (data: GanntTask) => void;
    closeMenu: () => void;
  }> = ({ task: taskVal, onColorChange }) => {
    const task = taskVal as any;
    if (task.type == "project") {
      return <></>;
    }
    const findUser = task?.assign?.find(
      (t: { user: { userId: string | undefined } }) =>
        t.user.userId == user?.userId
    );

    if (findUser && findUser.role == UserRoleEnumValue.COLLABORATOR) {
      return <></>;
    }

    const createDuplicate = (id: string) => {
      if (!validatePlan("project").valid) {
        return;
      }
      projectDuplicateMutation.mutate(id, {
        onSuccess(data) {
          toast.success(data.data.message);
          setData(prev => {
            if (data.data.data) {
              const newData = [...prev, data.data.data];
              setSpiPortfolioAndProgram(
                calculatePortfolioAndProgramSPIs(newData)
              );
              return newData;
            }
            return prev;
          });
          // projectQuery.refetch();
        },
        onError(error) {
          toast.error(
            errorKeyFormate(error.response?.data.message + ""),
            errorConfig
          );
        },
      });
    };
    const handleView = (id: string) => {
      const project = projects.find((p) => p.projectId === id);
      if (project && project.isActive === false) {
        setShowUpgradeModal(true);
        return;
      }
      navigate("/project-details/" + id);
    };
    const projectId = task.id.endsWith("-est")
      ? task.id.split("-est")[0]
      : task.id.endsWith("-act")
        ? task.id.split("-act")[0]
        : task.id.endsWith("-recalc")
          ? task.id.split("-recalc")[0]
          : task.id;
    const project = projects.find(p => p.projectId == projectId);

    const colors = [
      { id: "red", color: "#ef4444", title: "Red" },
      { id: "pink", color: "#ec4899", title: "Pink" },
      { id: "purple", color: "#a855f7", title: "Purple" },
      { id: "magenta", color: "#ff4dff", title: "Magenta" },
      { id: "violet", color: "#8b5cf6", title: "Violet" },
      { id: "indigo", color: "#6366f1", title: "Indigo" },
      { id: "blue", color: "#525DF9", title: "Blue" },
      { id: "cyan", color: "#06b6d4", title: "Cyan" },
      { id: "teal", color: "#14b8a6", title: "Teal" },
      { id: "green", color: "#22c55e", title: "Green" },
      { id: "gantt-green", color: "#15803d", title: "Gantt-green" },
      { id: "lime", color: "#84cc16", title: "Lime" },
      { id: "yellow", color: "#eab308", title: "Yellow" },
      { id: "orange", color: "#f97316", title: "Orange" },
      { id: "deep-orange", color: "#ff7043", title: "Deep-orange" },
      { id: "gray", color: "#6b7280", title: "Gray" },
      { id: "light-gray", color: "#d1d5db", title: "Light-gray" },
      { id: "null", color: "#525DF9", title: "No color", border: true },
    ];

    const activeColor = colors.find(c => c.color === (task.styles?.backgroundColor || task.ganttColor))?.id || "";

    const handleColorSelect = (colorId: string) => {
      const findColor = colors.find((c) => c.id == colorId);
      if (onColorChange) {
        onColorChange({
          ...task,
          styles: {
            backgroundColor: findColor?.color,
            backgroundSelectedColor: findColor?.color,
            progressColor: "#80808088",
            progressSelectedColor: "#80808088",
          },
        });
      }
    };

    return (
      <div className="w-fit flex flex-col gap-2 bg-white shadow-md rounded-md p-2">
        <div
          className="dropdown-menu-item flex cursor-pointer items-center"
          onClick={() => handleView(projectId)}
        >
          <Settings className="mr-2 h-4 w-4 text-[#44546F]" />
          <span className="p-0 font-normal h-auto">{t("project_settings")}</span>
        </div>
        {
          project &&
          <button
            disabled={!verification(project)}
            className={`dropdown-menu-item flex cursor-pointer items-center ${!verification(project) ? " opacity-50" : ""}`}
            onClick={() => setProjectId(projectId)}
          >
            <ScrollText className="mr-2 h-4 w-4 text-[#44546F]" />
            <span className="p-0 font-normal h-auto">{t("create_task")}</span>
          </button>
        }


        {project && (
          <div
            className={`dropdown-menu-item flex items-center ${canDuplicateOrRemoveThisProject(project)
                ? "cursor-pointer"
                : "cursor-not-allowed opacity-50"
              }`}
            onClick={() => {
              if (canDuplicateOrRemoveThisProject(project)) {
                createDuplicate(projectId);
              }
            }}
          >
            <img src={DuplicateIcon} className="h-4 w-4 mr-2" />
            <span className="p-0 font-normal h-auto">{t("duplicate_project")}</span>
          </div>
        )}

        <Link
          className="dropdown-menu-item flex cursor-pointer items-center"
          to={"/members/" + projectId}
        >
          <img src={Users} className="h-4 w-4 mr-2" />
          <span className="p-0 font-normal h-auto">{t("ProjectMembers")}</span>
        </Link>

        {project && (
          <div
            className={`dropdown-menu-item flex items-center ${canDuplicateOrRemoveThisProject(project)
                ? "cursor-pointer"
                : "cursor-not-allowed opacity-50"
              }`}
            onClick={() => {
              if (canDuplicateOrRemoveThisProject(project)) {
                setRemove(projectId);
              }
            }}
          >
            <img src={TrashCan} className="h-4 w-4 mr-2" />
            <span className="p-0 font-normal h-auto text-red-500">
              {t("remove_project")}
            </span>
          </div>
        )}

        {project && verification(project) && (
          <>
            <Popover>
              <PopoverTrigger className="dropdown-menu-item flex items-center w-full cursor-pointer bg-white">
                <img src={ColorPickerIcon} className="h-4 w-4 mr-2 opacity-80" />
                <span className="p-0 font-normal h-auto">{t("change_color")}</span>
              </PopoverTrigger>
              <PopoverContent side="right" style={{ zIndex: 9999 }}>
                <div className="relative overflow-hidden bg-white rounded-md w-64 h-fit border border-gray-200 shadow-md">
                  <ul
                    className="grid grid-cols-6 gap-2 p-2 overflow-auto"
                    role="listbox"
                    aria-activedescendant="active-color"
                  >
                    {colors.map((color) => (
                      <li
                        onClick={() => handleColorSelect(color.id)}
                        key={color.id}
                        className={`flex items-center justify-center w-8 h-8 cursor-pointer rounded-full material-icons ${
                          color.border ? "border border-gray-300" : ""
                        }`}
                        style={{
                          backgroundColor: color.id !== "null" ? color.color : "",
                        }}
                        role="option"
                        aria-selected={color.id === activeColor ? "true" : "false"}
                        data-id={color.id}
                        title={color.title}
                        id={color.id === activeColor ? "active-color" : undefined}
                      >
                        {color.id == "null" ? <i></i> : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}
      </div>
    );
  };
  const StandardTooltipContent: React.FC<{
    task: any;
    fontSize: string;
    fontFamily: string;
  }> = ({ task }) => {
    if (task.ganttRoadmapMilestone) {
      const mStart =
        task.start instanceof Date && !Number.isNaN(task.start.getTime())
          ? task.start
          : new Date();
      return (
        <div className="relative w-60 max-w-[250px]">
          <h3 className="text-lg font-semibold mb-1">{task.name}</h3>
          <div className="flex justify-between text-sm mt-1">
            <span>{t("Date")}:</span>
            <span>{dateFormater(mStart)}</span>
          </div>
        </div>
      );
    }

    const taskType = task.id.endsWith("-est")
      ? "Estimated"
      : task.id.endsWith("-act")
        ? "Actual"
        : task.id.endsWith("-recalc")
          ? "Recalculated"
          : "Comparison";

    let startDate: Date;
    let endDate: Date;
    let duration: number = 1;
    const project = projects.find((t) =>
      String(task.id).includes(t.projectId)
    ) as Project;
    switch (taskType) {
      case "Estimated":
        startDate = new Date(task.startDate ?? task.start);
        endDate = new Date(task.estimatedEndDate ?? task.end);
        endDate.setDate(endDate.getDate() - 1);
        duration = project?.estimatedDuration ?? 1;
        break;
      case "Actual":
        startDate = new Date(task.actualStartDate ?? task.start);
        endDate = new Date(task.actualEndDate ?? task.end);
        endDate.setDate(endDate.getDate() - 1);
        duration = project?.actualDuration ?? 1;
        break;
      case "Recalculated":
        startDate = new Date(task.actualStartDate ?? task.start);
        endDate = new Date(task.reCalculateEndDate ?? task.end);
        endDate.setDate(endDate.getDate() - 1);
        duration = project?.reCalculatedDuration ?? 1;
        break;
      default:
        startDate = new Date(task.start);
        endDate = new Date(task.end);
        endDate.setDate(endDate.getDate() - 1);
        duration =
          selectedDateType == "actual"
            ? project?.actualDuration ?? 1
            : project?.estimatedDuration ?? 1;
    }
    // Bar task always carries the duration used to render the bar (leaf tasks are type "task")
    if (typeof task.duration === "number" && Number.isFinite(task.duration)) {
      duration = task.duration;
    }

    // Adjust for inclusive display
    let displayEndDate = new Date(endDate);
    if ((task.duration > 0 ? task.duration : 1) == 1) {
      displayEndDate = startDate;
    }

    return (
      <div className="relative  w-60 max-w-[250px]">
        <h3 className="text-lg font-semibold mb-2">{task.name}</h3>
        {taskType !== "Comparison" && (
          <p className="text-sm text-gray-500 italic mb-2">Type: {taskType}</p>
        )}

        <div className="flex justify-between text-sm">
          <span>{t("start_date")}:</span>
          <span>{dateFormater(startDate)}</span>
        </div>

        <div className="flex justify-between text-sm">
          <span>{t("end_date")}:</span>
          <span>{dateFormater(displayEndDate)}</span>
        </div>

        <div className="flex justify-between text-sm">
          <span>{t("Duration")}:</span>
          <span>{duration > 0 ? Number(duration).toFixed() : 1} days</span>
        </div>
        <div className="flex justify-between items-center text-nowrap text-sm mt-0">
          <span>{taskType == "Estimated" ? t("Expected progress") : taskType == "Actual" ? t("Current progress") : t("progress")}:</span>
          <div className="w-full bg-[#78748649] h-2 rounded-lg overflow-hidden ml-3">
            <div
              className="bg-[#535EF9] h-full"
              style={{ width: `${task.progress}%` }}
            ></div>
          </div>
          <span className="ml-2">{task.progress?.toFixed()}%</span>
        </div>
      </div>
    );
  };
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem("projects_hidden_columns");

      if (!stored) {
        localStorage.setItem(
          "projects_hidden_columns",
          JSON.stringify([
            "actual_start_date",
            "actual_end_date",
            "Re_calculated_end_date",
            "spi",
            "cpi",
            "estimated_budget",
            "consumed_budget",
            "Re-calculated_Budget",
          ])
        );
      }
      return stored
        ? JSON.parse(stored)
        : [
          "actual_start_date",
          "actual_end_date",
          "Re_calculated_end_date",
          "spi",
          "cpi",
          "estimated_budget",
          "consumed_budget",
          "Re-calculated_Budget",
        ];
    } catch (e) {
      // Ignore invalid/missing localStorage content
    }
  });
  const [sortCol, setSortCol] = useState<any>([]);

  const uniqueHiddenColumns = useMemo(
    () => Array.from(new Set(hiddenColumns)),
    [hiddenColumns, sortCol]
  );

  function sanitizeProjectName(name: string): string {
    const labels = [`(${t("Estimated")})`, `(${t("Current")})`];

    let cleaned = name;

    for (const label of labels) {
      const firstIndex = cleaned.indexOf(label);

      if (firstIndex !== -1) {
        // Remove ONLY the first occurrence
        cleaned =
          cleaned.slice(0, firstIndex) +
          cleaned.slice(firstIndex + label.length);

        cleaned = cleaned.trim();
      }
    }

    // Normalize extra spaces
    return cleaned.replace(/\s+/g, " ").trim();
  }
  const projectDtails = (id: string) => {
    const projectId = id.endsWith("-est")
      ? id.split("-est")[0]
      : id.endsWith("-act")
        ? id.split("-act")[0]
        : id.endsWith("-recalc")
          ? id.split("-recalc")[0]
          : id;
    return data.find((item: any) => item.projectId == projectId);
  };

  const roadmapTableGroupCell = (
    <div className="flex w-full items-center justify-center">
      <span className="text-base font-medium text-gray-400">-</span>
    </div>
  );
  const isRoadmapTableGroupRow = (taskId: string) => !projectDtails(taskId);

  const getStatusColor = (status: string) => {
    switch (status) {
      case ProjectStatusEnumValue.NOT_STARTED:
        return "bg-[#FF8C00]/20 text-[#FF8C09]";
      case ProjectStatusEnumValue.ON_HOLD:
        return "bg-[#FAB415]/20 text-[#B58109]";
      case ProjectStatusEnumValue.ACTIVE:
      case ProjectStatusEnumValue.IN_PROGRESS:
        return "bg-[#53C36C]/20 text-[#15803D]";
      case ProjectStatusEnumValue.CLOSED:
      case ProjectStatusEnumValue.COMPLETED:
        return "bg-[#828F9B]/20 text-[#828F89]";
      case ProjectStatusEnumValue.CANCELLED:
        return "bg-[#EF4444]/20 text-[#DC2626]";
    }
  };
  const allCols = useMemo(() => {
    const scheduleIndicatorDelayRange = {
      onTimeDelayMax:
        user?.userOrganisation?.[0]?.organisation?.onTimeDelayMax ?? 5,
      moderateDelayMax:
        user?.userOrganisation?.[0]?.organisation?.moderateDelayMax ?? 20,
    };

    const col: columnsType[] = [
      {
        field: "name",
        name: t("project_name"),
        sortable: true,
        width: 380,
        render(data: any) {
          const isGroup = isRoadmapTableGroupRow(data.id);
          const groupNode = isGroup
            ? findRoadmapHierarchyGroupNode(hierarchyData, data.id)
            : undefined;

          return (
            <div
              key={data.id}
              className={`${data.project ? "" : ""
                } flex items-center gap-1 w-full`}
            >
              {/* {data.flag == "Green" ? (
                              <img src={GreenFlag} className="h-3 w-3" />
                            ) : data.flag == "Red" ? (
                              <img src={RedFlag} className="h-3 w-3" />
                            ) : data.flag == "Orange" ? (
                              <img src={OrangeFlag} className="h-3 w-3" />
                            ) : (
                              ""
                            )} */}
              <div className="w-full break-words">
                {isGroup && groupNode ? (
                  <div className="flex items-center gap-2">
                    {showInput === data.id ? (
                      <div className="flex gap-2 items-center">
                        <InputText
                          className="mt-0 my-2 h-[40px]"
                          defaultValue={
                            groupNode.type === "portfolio"
                              ? groupNode.portfolioName
                              : groupNode.programName
                          }
                          onKeyDown={(e) =>
                            e.key === "Enter" ? updatePortfolioAndProgram() : ""
                          }
                          onChange={(e) => {
                            editValueRef.current = e.target.value;
                          }}
                        />
                        <span
                          className="cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            updatePortfolioAndProgram();
                          }}
                        >
                          <img src={CheckedSvg} className="w-5 max-w-5" />
                        </span>
                        <span
                          className="font-bold text-sm text-red-400 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUpdateValue(undefined);
                            setShowInput("");
                          }}
                        >
                          <img src={CloseCircle} className="w-5 max-w-5" />
                        </span>
                      </div>
                    ) : (
                      <div className="flex gap-2 items-center">
                        <div className="h-10 flex items-center truncate">
                          {groupNode.type === "portfolio"
                            ? groupNode.portfolioName
                            : groupNode.programName}
                        </div>
                        {user &&
                          ((groupNode.type === "portfolio" &&
                            canManagePortfolioHierarchyRow(
                              user,
                              groupNode.portfolioId,
                            )) ||
                            (groupNode.type === "program" &&
                              canManageProgramHierarchyRowEnhanced(
                                user,
                                groupNode.programId,
                                groupNode.portfolioId,
                              ))) && (
                            <div className="row-action-buttons flex gap-1 items-center">
                              <img
                                src={NewEditIcon}
                                className="h-4 w-4 cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowInput(data.id);
                                  setUpdateValue({
                                    type:
                                      groupNode.type === "portfolio"
                                        ? "portfolio"
                                        : "program",
                                    id: data.id,
                                  });
                                  editValueRef.current =
                                    groupNode.type === "portfolio"
                                      ? groupNode.portfolioName
                                      : groupNode.programName;
                                }}
                              />
                              <img
                                src={TrashCanV2}
                                className="h-4 w-4 cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletePortfolioAndProgram({
                                    portfolio:
                                      groupNode.type === "portfolio"
                                        ? groupNode.portfolioId
                                        : "",
                                    program:
                                      groupNode.type === "program"
                                        ? groupNode.programId
                                        : "",
                                  });
                                }}
                              />
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>{sanitizeProjectName(data.name)}</span>
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        field: "assign",
        name: t("Manager"),
        width: 155,
        render(data: any) {
          if (isRoadmapTableGroupRow(data.id)) {
            const groupNode = findRoadmapHierarchyGroupNode(
              hierarchyData,
              data.id,
            );
            const manager = groupNode?.assignedUsers?.[0];
            return manager ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="ml-3 flex">
                      <div>
                        <UserAvatar
                          className="shadow-sm shadow-gray-300 border-2 border-white"
                          user={manager?.user}
                        />
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-sm space-y-0.5">
                      <div>
                        {t("Role")}:{" "}
                        {t(
                          (
                            (manager as any)?.portfolioRole ||
                            (manager as any)?.programRole ||
                            ""
                          ).toLowerCase(),
                        )}
                      </div>
                      {manager?.user.firstName && manager?.user.lastName && (
                        <div>
                          {t("name")}:{" "}
                          {`${manager?.user.firstName} ${manager?.user.lastName}`}
                        </div>
                      )}
                      <div>
                        {t("Email")}: {manager?.user.email}
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              roadmapTableGroupCell
            );
          }
          const userPm = data.assign?.find((u: any) => {
            return u?.role == UserRoleEnumValue.PROJECT_MANAGER;
          });
          return userPm ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="ml-3 flex">
                    <div>
                      <UserAvatar
                        className="shadow-sm shadow-gray-300 border-2 border-white"
                        user={userPm?.user}
                      />
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="text-sm space-y-0.5">
                    <div>
                      {t("Role")}: {t((userPm?.role || "").toLowerCase())}
                    </div>
                    {userPm?.user.firstName && userPm?.user.lastName && (
                      <div>
                        {t("Name")}:{" "}
                        {`${userPm?.user.firstName} ${userPm?.user.lastName}`}
                      </div>
                    )}
                    <div>
                      {t("Email")}: {userPm?.user.email}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <></>
          );
        },
      },
      {
        field: "progressionPercentage",
        name: t("Actual Progress"),
        width: 155,
        sortable: true,
        render: (project: any) => {
          if (isRoadmapTableGroupRow(project.id)) {
            const groupNode = findRoadmapHierarchyGroupNode(
              hierarchyData,
              project.id,
            );
            const actualValue = groupNode ? groupNode.actualProgress ?? 0 : 0;
            return (
              <div className="flex w-full items-center justify-center">
                <PercentageCircle percentage={actualValue} />
              </div>
            );
          }
          const item: any = projectDtails(project.id);
          if (!item) {
            return roadmapTableGroupCell;
          }

          const spiColor = getSpiScheduleIndicatorHex(
            item.spi,
            scheduleIndicatorDelayRange,
          );

          const actualPct = Math.round(
            Number(item?.progressionPercentage ?? 0) * 100,
          );
          return (
            <div className="w-full flex justify-center">
              <PercentageCircle
                percentage={actualPct}
                roundColor={spiColor}
              />
            </div>
          );
        },
      },
      {
        field: "progress",
        name: t("Expacted Progress"),
        sortable: true,
        width: 155,
        render(data: any) {
          if (isRoadmapTableGroupRow(data.id)) {
            const groupNode = findRoadmapHierarchyGroupNode(
              hierarchyData,
              data.id,
            );
            const actualValue = groupNode ? groupNode.actualProgress ?? 0 : 0;
            const expectedValue = groupNode ? groupNode.expectedProgress ?? 0 : 0;
            return (
              <div className="flex justify-center w-full">
                <PercentageCircle
                  percentage={expectedValue}
                  roundColor={getPercentageCircleDefaultBandHex(actualValue)}
                />
              </div>
            );
          }
          const project = projectDtails(data.id) as Project | undefined;
          if (!project) {
            return roadmapTableGroupCell;
          }
          const expectedPct = (() => {
            const n = Number((project as any).expectedprogress);
            return Number.isFinite(n) ? n : 0;
          })();

          const spiForSchedule =
            typeof project.spi === "number"
              ? project.spi
              : spiPortfolioAndProgram?.[data.id];
          const spiColor = getSpiScheduleIndicatorHex(
            spiForSchedule,
            scheduleIndicatorDelayRange,
          );

          return (
            <div className="flex justify-center w-full">
              <PercentageCircle
                percentage={expectedPct}
                roundColor={spiColor}
              />
            </div>
          );
        },
      },

      {
        field: "status",
        name: t("status"),
        sortable: true,
        width: 100,
        render: (item: any) => {
          if (isRoadmapTableGroupRow(item.id)) {
            return roadmapTableGroupCell;
          }
          const project = projectDtails(item.id);

          return project ? (
            <>
              <div
                className={`w-24 h-8 px-3 py-1.5 ${getStatusColor(
                  project?.status,
                )} rounded-full justify-center items-center gap-px inline-flex`}
              >
                <div className=" text-xs font-medium leading-tight">
                  {t(project?.status)
                  }
                </div>
              </div>
            </>
          ) : (
            roadmapTableGroupCell
          );
        },
      },
      {
        field: "start",
        sortable: true,
        name: t("estimated_start_date"),
        width: 155,
        render(data: any) {
          if (isRoadmapTableGroupRow(data.id)) {
            return roadmapTableGroupCell;
          }
          const id = data.id.endsWith("-est")
            ? data.id.split("-est")[0]
            : data.id.endsWith("-act")
              ? data.id.split("-act")[0]
              : data.id.endsWith("-recalc")
                ? data.id.split("-recalc")[0]
                : data.id;
          const project = projects.find((p) => p.projectId == id);
          const startDate = project?.startDate;
          const formatted =
            startDate && String(startDate).trim() !== ""
              ? (() => {
                const safeDate = new Date(startDate as any);
                return safeDate && !isNaN(safeDate.getTime())
                  ? dateFormater(safeDate)
                  : "NA";
              })()
              : "NA";
          return (
            <div className="w-full flex justify-center items-center">
              {formatted}
            </div>
          );
        },
      },
      {
        field: "end",
        sortable: true,
        name: t("estimated_end_date"),
        width: 155,
        render(data: any) {
          if (isRoadmapTableGroupRow(data.id)) {
            return roadmapTableGroupCell;
          }
          const id = data.id.endsWith("-est")
            ? data.id.split("-est")[0]
            : data.id.endsWith("-act")
              ? data.id.split("-act")[0]
              : data.id.endsWith("-recalc")
                ? data.id.split("-recalc")[0]
                : data.id;
          const project = projects.find((p) => p.projectId == id);
          const estimatedEndDate = project?.estimatedEndDate;
          const safeDate = new Date(estimatedEndDate as any);
          return (
            <div className="w-full flex justify-center items-center">
              {estimatedEndDate &&
                String(estimatedEndDate).trim() !== "" &&
                safeDate &&
                !isNaN(safeDate.getTime())
                ? dateFormater(safeDate)
                : "NA"}
            </div>
          );
        },
      },

      {
        field: "estimated_duration",
        name: t("estimated_duration"),
        width: 155,
        render(data: any) {
          if (isRoadmapTableGroupRow(data.id)) {
            return roadmapTableGroupCell;
          }
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => p.projectId == result);

          return (
            <div className="w-full flex justify-center items-center">
              {project?.estimatedDuration && project?.estimatedDuration >= 0
                ? Number(project?.estimatedDuration).toFixed()
                : "-"}
            </div>
          );
        },
      },
      {
        field: "actual_duration",
        name: t("actual_duration"),
        width: 155,
        render(data: any) {
          if (isRoadmapTableGroupRow(data.id)) {
            return roadmapTableGroupCell;
          }
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => p.projectId == result);

          return (
            <div className="w-full flex justify-center items-center">
              {project?.actualDuration && project?.actualDuration >= 0
                ? Number(project?.actualDuration).toFixed()
                : "-"}
            </div>
          );
        },
      },
      {
        field: "Re_calculated_duration",
        name: t("Re_calculated_duration"),
        width: 155,
        render(data: any) {
          if (isRoadmapTableGroupRow(data.id)) {
            return roadmapTableGroupCell;
          }
          const project = projectDtails(data.id) as Project | undefined;
          if (!project) {
            return (
              <div className="w-full flex justify-center items-center">-</div>
            );
          }
          const fromApi = project.reCalculatedDuration;
          let display: string;
          if (typeof fromApi === "number" && Number.isFinite(fromApi)) {
            display = Number(fromApi).toFixed(0);
          } else if (project.reCalculateEndDate) {
            const startRaw = project.actualStartDate ?? project.startDate;
            if (startRaw) {
              const s = new Date(startRaw);
              const e = new Date(project.reCalculateEndDate);
              if (!isNaN(s.getTime()) && !isNaN(e.getTime())) {
                const d = calculateDurations(s, e);
                display = d >= 0 ? String(d) : "-";
              } else {
                display = "-";
              }
            } else {
              display = "-";
            }
          } else {
            display = "-";
          }
          return (
            <div className="w-full flex justify-center items-center">
              {display}
            </div>
          );
        },
      },
      {
        field: "actual_start_date",
        name: t("actual_start_date"),
        width: 155,
        render(data: any) {
          if (isRoadmapTableGroupRow(data.id)) {
            return roadmapTableGroupCell;
          }
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => p.projectId == result);
          return (
            <div className="w-full flex justify-center items-center">
              {project?.actualStartDate
                ? dateFormater(new Date(project?.actualStartDate))
                : "-"}
            </div>
          );
        },
      },
      {
        field: "actual_end_date",
        name: t("actual_end_date"),
        width: 155,

        render(data: any) {
          if (isRoadmapTableGroupRow(data.id)) {
            return roadmapTableGroupCell;
          }
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => p.projectId == result);
          return (
            <div className="w-full flex justify-center items-center">
              {project?.actualEndDate
                ? dateFormater(new Date(project?.actualEndDate))
                : "-"}
            </div>
          );
        },
      },
      {
        field: "Re_calculated_end_date",
        name: t("Re_calculated_end_date"),
        width: 165,
        render(data: any) {
          if (isRoadmapTableGroupRow(data.id)) {
            return roadmapTableGroupCell;
          }
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => p.projectId == result);
          return (
            <div className="w-full flex justify-center items-center">
              {project?.reCalculateEndDate
                ? dateFormater(new Date(project?.reCalculateEndDate))
                : "-"}
            </div>
          );
        },
      },
      {
        field: "spi",
        name: t("spi"),
        width: 50,
        render(data: any) {
          if (isRoadmapTableGroupRow(data.id)) {
            return roadmapTableGroupCell;
          }
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => {
            return p.projectId == result
          });
          return (
            <div className="w-full flex justify-center items-center">
              {Number(project?.spi ?? 0).toFixed()}
            </div>
          );
        },
      },
      {
        field: "cpi",
        name: t("cpi"),
        width: 50,
        render(data: any) {
          if (isRoadmapTableGroupRow(data.id)) {
            return roadmapTableGroupCell;
          }
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => p.projectId == result);
          return (
            <div className="w-full flex justify-center items-center">
              {Number(project?.cpi ?? 0).toFixed()}
            </div>
          );
        },
      },
      {
        field: "estimated_budget",
        name: t("estimated_budget"),
        width: 155,
        render(data: any) {
          if (isRoadmapTableGroupRow(data.id)) {
            return roadmapTableGroupCell;
          }
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => {
            return p.projectId == result
          });
          return (
            <div className="w-full flex justify-center items-center">
              {project?.estimatedBudget
                ? project?.estimatedBudget == "undefined"
                  ? "-"
                  : project?.estimatedBudget
                : "-"}
            </div>
          );
        },
      },
      {
        field: "consumed_budget",
        name: t("consumed_budget"),
        width: 155,
        render(data: any) {
          if (isRoadmapTableGroupRow(data.id)) {
            return roadmapTableGroupCell;
          }
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => {
            return p.projectId == result
          });
          return (
            <div className="w-full flex justify-center items-center">
              {project?.consumedBudget
                ? project?.consumedBudget == "undefined"
                  ? "-"
                  : Number(project?.consumedBudget).toFixed()
                : "-"}
            </div>
          );
        },
      },
      {
        field: "Re-calculated_Budget",
        name: t("Re-calculated_Budget"),
        width: 155,
        render(data: any) {
          if (isRoadmapTableGroupRow(data.id)) {
            return roadmapTableGroupCell;
          }
          const str = data.id;
          const result = str.substring(0, str.length - 4);
          const project = projects.find((p) => {
            return p.projectId == result
          });
          return (
            <div className="w-full flex justify-center items-center">
              {project?.reCalculateBudget
                ? project?.reCalculateBudget == "undefined"
                  ? "-"
                  : Number(project?.reCalculateBudget).toFixed(0)
                : "-"}
            </div>
          );
        },
      },
    ];

    if (uniqueHiddenColumns.length > 0) {
      col.push({
        field: "add new column",
        name: t("add_column"),
        width: 160,
        headerRender() {
          return (
            <DropdownMenu>
              <DropdownMenuTrigger>{t("add_column")} </DropdownMenuTrigger>
              <DropdownMenuContent>
                {uniqueHiddenColumns.map((key) => {
                  const field = col.find((t: any) => t.field == key)?.name;
                  return (
                    <DropdownMenuItem key={key} onSelect={() => AddColumn(key)}>
                      <div className="h-full w-full cursor-pointer">
                        {t(field ?? "")}
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        render() {
          return <></>;
        },
      });
    }
    if (!sortCol.length) {
      setSortCol(
        col.map((t) => {
          return { field: t.field, width: t.width };
        })
      );
    }
    return col;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- render fns use projectDtails + group helpers; `data` is listed
  }, [
    t,
    projects,
    data,
    hiddenColumns,
    sortCol,
    spiPortfolioAndProgram,
    calculateDurations,
    hierarchyData,
    showInput,
    deletePortfolioAndProgram,
    user,
  ]);

  useEffect(() => {
    // columns that were unhidden
    const unhidden = allCols.filter(
      (col) => !uniqueHiddenColumns.includes(col.field)
    );

    // find specific column
    const find = unhidden.find((t) => t.field === "add new column");

    if (find) {
      setSortCol((prev: any) => {
        if (!prev.some((t: any) => t.field === find.field)) {
          return [...prev, { field: find.field, width: find.width }];
        }
        return prev;
      });
    }
  }, [uniqueHiddenColumns, allCols]);

  const visibleCols = useMemo(() => {
    const filteredCols = allCols.filter(
      (c: any) => !hiddenColumns.includes(c.field)
    );
    if (sortCol && sortCol.length > 0) {
      const sortedCol = sortCol
        .map((element: any) =>
          filteredCols.find((t) => t.field == element.field)
            ? {
              ...filteredCols.find((t) => t.field == element.field)!,
              width: element.width,
            }
            : null
        )
        .filter(Boolean); // remove nulls in case sortCol has old fields
      return sortedCol.length > 0 ? sortedCol : filteredCols;
    }
    return filteredCols;
  }, [allCols, hiddenColumns, sortCol]);

  const AddColumn = (field: string) => {
    if (hiddenColumns.includes(field)) {
      const updated = hiddenColumns.filter((item) => item !== field);
      localStorage.setItem("projects_hidden_columns", JSON.stringify(updated));

      const findcol = allCols.find((item) => item.field === field);
      if (findcol) {
        setSortCol((prev: any) => {
          // Always work from the current allCols order
          const baseCols = prev.length
            ? prev
            : allCols.map((c) => ({ field: c.field, width: c.width }));

          // Avoid duplicates
          if (baseCols.some((c: any) => c.field === findcol.field))
            return baseCols;

          // Insert before the "add new column" field if it exists
          const insertIndex = Math.max(baseCols.length - 1, 0);
          const newCol = [...baseCols];
          newCol.splice(insertIndex, 0, {
            field: findcol.field,
            width: findcol.width,
          });
          return newCol;
        });
      }

      setHiddenColumns(updated);
    }
  };

  const hideColumn = (field: any[]) => {

    setSortCol(
      field.map((t) => {
        return { field: t.field, width: t.width };
      })
    );
    const stored = localStorage.getItem("projects_hidden_columns") ?? "";
    setHiddenColumns(JSON.parse(stored));
  };
  useEffect(() => {
    updateHeight();
  }, [hiddenColumns, allCols, visibleCols, sortCol, compare, showMilestones]);

  return (
    <div className={`w-full ${isOpenWorkLoad ? "h-[75%]" : "h-[90%]"}`} ref={Divref}>
      <style>{`
        .row-action-hover-group .row-action-buttons {
          display: none !important;
        }
        .row-action-hover-group:hover .row-action-buttons {
          display: flex !important;
        }
      `}</style>
      {view && (
        <div
          ref={ganttPdfRef}
        >
          <Gantt
            rowHeight={ganttRoadmapRowLayout.rowHeight}
            barFill={ganttRoadmapRowLayout.barFill}
            canCreateDependency={false}
            key={GanttHeight.current}
            startEndIndicater={false}
            ganttHeight={GanttHeight.current - 50}
            onExpanderClick={(e) => expandToggle(e.id)}
            newTask={undefined}
            tasks={convertedTasks}
            taskSorted={setConvertedTasks as any}
            columns={visibleCols}
            projectStartDate={ganttDate.start}
            projectEndDate={findLatestOverallEndDate(projects) ?? ganttDate.end}
            preStepsCount={2}
            showWorkload={isOpenWorkLoad}
            holidays={
              user?.userOrganisation[0].organisation.orgHolidays?.map(
                (h) => new Date(h.holidayStartDate)
              ) ?? []
            }
            setColumns={hideColumn}
            viewMode={view}
            nonWorkingDays={nonWorkingDays()}
            showContextManu={true}
            onContextManuEvent={function (): void { }}
            onScroll={function (): void { }}
            onDepandanyClick={function (): void { }}
            onColorChange={(e) => {
              const projectId = e.id.endsWith("-est")
                ? e.id.split("-est")[0]
                : e.id.endsWith("-act")
                  ? e.id.split("-act")[0]
                  : e.id.endsWith("-recalc")
                    ? e.id.split("-recalc")[0]
                    : e.id;

              setData((prev) =>
                prev.map((project) =>
                  project.projectId === projectId
                    ? {
                        ...project,
                        ganttColor: e.styles?.backgroundColor,
                      }
                    : project
                )
              );

              projectColorUpdateMutation.mutate({
                projectId: projectId,
                ganttColor: e.styles?.backgroundColor,
              }, {
                onError(error) {
                  const err = error.response?.data;
                  toast.error(
                    errorKeyFormate(err?.message ?? "Unknown error"),
                    errorConfig
                  );
                },
              });
            }}
            onDepandancyDraging={function (): void { }}
            onDepandancyDragEnd={function (): void { }}
            onDateChange={
              selectedDateType !== "actual"
                ? handleProjectDateChange
                : undefined
            }
            onDoubleClick={(e) => {
              if (e.type == "task") {
                const projectId = e.id.endsWith("-est")
                  ? e.id.split("-est")[0]
                  : e.id.endsWith("-act")
                    ? e.id.split("-act")[0]
                    : e.id.endsWith("-recalc")
                      ? e.id.split("-recalc")[0]
                      : e.id;
                const project = projects.find((p) => p.projectId === projectId);
                if (project && project.isActive === false) {
                  setShowUpgradeModal(true);
                  return;
                }
                if (!project) {
                  return;
                }
                const navbar = document.getElementById("navbar");
                const sidebar = document.getElementById("Sidebar");
                if (navbar) navbar.classList.remove("!hidden");
                if (sidebar) sidebar.classList.remove("!hidden");
                if (
                  user?.subscription?.planType == PlanTypeEnumSchema.FREE ||
                  !user?.subscription
                ) {
                  navigate("/tasks/" + projectId);
                } else {
                  navigate("/projectDashboard/" + projectId);
                }
              }
            }}
            onProgressChange={undefined}
            TooltipContent={StandardTooltipContent}
            ContextMenuContent={contextmanu}
          />
        </div>
      )}
      <Dialog
        isOpen={Boolean(remove)}
        onClose={() => setRemove(undefined)}
        modalClass="rounded-lg"
      >
        <div className="flex flex-col gap-2 p-6 ">
          <img src={TrashCan} className="w-12 m-auto" />{" "}
          {t("common_delete_message")}
          <div className="flex gap-2 ml-auto">
            <Button
              variant={"outline"}
              isLoading={removeProjectMutation.isPending}
              disabled={removeProjectMutation.isPending}
              onClick={() => setRemove(undefined)}
            >
              {t("cancel")}
            </Button>
            <Button
              variant={"primary"}
              className="text-white"
              onClick={removeProject}
            >
              {t("delete")}
            </Button>
          </div>
        </div>
      </Dialog>
      {projectId && (
        <TaskSubTaskForm
          taskId={undefined}
          projectId={projectId}
          close={() => setProjectId("")}
        />
      )}
      <DeletePortfolioProgramConfirmDialog
        isOpen={!!deletePortfolioAndProgram}
        isPortfolio={!!deletePortfolioAndProgram?.portfolio}
        isLoading={
          removePortfolioMutation.isPending ||
          removeProgramMutation.isPending
        }
        trashIcon={TrashCanV2}
        onClose={() => setDeletePortfolioAndProgram(undefined)}
        onConfirm={handleRemovePortfolioAndProgram}
      />
      {showUpgradeModal && (
        <Dialog
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          modalClass="rounded-lg "
          bgClass="!backdrop-blur-sm"
        >
          <div className="flex flex-col gap-4 p-6 w-96 text-center">
            {(!user?.userOrganisation[0]?.role ||
              user?.userOrganisation[0]?.role ==
              UserRoleEnumValue.COLLABORATOR) ? (
              t("You do not have access to this dashboard with your current plan. Please contact your administrator to upgrade the current plan")
            ) : (
              t("Project dashboard are not available in this plan. Do you want to upgrade your plan?")
            )}
            <div className="flex gap-2 ml-auto">
              {(!user?.userOrganisation[0]?.role ||
                user?.userOrganisation[0]?.role ==
                UserRoleEnumValue.COLLABORATOR) ? (
                <Button
                  variant={"outline"}
                  onClick={() => {
                    setShowUpgradeModal(false);
                  }}
                >
                  {t("ok")}
                </Button>
              ) : (
                <>
                  <Button
                    variant={"outline"}
                    onClick={() => {
                      setShowUpgradeModal(false);
                    }}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    variant={"primary"}
                    className="text-white"
                    onClick={() => {
                      navigate("/plans");
                    }}
                  >
                    {t("Upgrade")}
                  </Button>
                </>
              )}
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}

export default GanttView;
